import { isArray } from 'lodash';
import { ReactNode } from 'react';
import { Stack, Typography } from '@common-components';
import { messages } from 'i18n';
import { ActivityLog } from 'types';
import { normalizeFieldValueForDisplay, parseFieldValue } from './field-display-normalizers';

/** Treats only null, undefined, '', and empty {} or [] as empty. Numbers and booleans (e.g. 0, false) are not empty. */
function isDisplayEmpty(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (typeof value === 'object' && Object.keys(value as object).length === 0) {
    return true;
  }
  return false;
}

function renderParsedValue(parsed: unknown): { inline: boolean; content: ReactNode } {
  if (isDisplayEmpty(parsed)) {
    return { inline: true, content: '—' };
  }
  if (isArray(parsed)) {
    return { inline: true, content: parsed.join(', ') };
  }
  if (typeof parsed === 'object') {
    return {
      inline: false,
      content: (
        <Stack component="span" direction="column" pl={1}>
          {Object.entries(parsed as Record<string, unknown>).map(([key, val]) => (
            // Assuming object with one level nesting, not nested objects
            <Typography key={key} variant="caption" color="text.secondary">
              • {key}: {isDisplayEmpty(val) ? '—' : String(val)}
            </Typography>
          ))}
        </Stack>
      ),
    };
  }
  return { inline: true, content: String(parsed) };
}

interface CarrierSubmissionUpdateActivityProps {
  activityLog: ActivityLog;
}

export function CarrierSubmissionUpdateActivity({ activityLog }: CarrierSubmissionUpdateActivityProps) {
  const changedFields = activityLog.metadata?.changedFields;
  if (!changedFields?.length) {
    return null;
  }

  const fieldLabels = messages.submissionActivityLog.carrierSubmissionFieldLabels;
  return (
    <>
      {changedFields.map(({ field, newValue }) => {
        const label = fieldLabels[field as keyof typeof fieldLabels] ?? field;
        const parsed = normalizeFieldValueForDisplay(field, parseFieldValue(newValue));
        const { inline, content } = renderParsedValue(parsed);

        return (
          <Stack key={field} direction="column">
            <Typography variant="caption" color="text.secondary">
              <strong>{label}:</strong>
              {inline && <> {content}</>}
            </Typography>
            {!inline && content}
          </Stack>
        );
      })}
    </>
  );
}
